import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import type { ChatTurnMessage } from './types'

const BOTTOM_THRESHOLD_PX = 80

type Options = {
    messages: ChatTurnMessage[]
    loading: boolean
    conversationId?: string | null
    /** When false, skip auto-scroll (e.g. panel closed). */
    enabled?: boolean
}

function isNearBottom(el: HTMLElement): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX
}

export function useChatPanelAutoScroll({ messages, loading, conversationId, enabled = true }: Options) {
    const scrollableNodeRef = useRef<HTMLElement | null>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const pinnedToBottomRef = useRef(true)

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        const el = scrollableNodeRef.current
        if (!el) return
        el.scrollTo({ top: el.scrollHeight, behavior })
    }, [])

    const handleScroll = useCallback(() => {
        const el = scrollableNodeRef.current
        if (!el) return
        pinnedToBottomRef.current = isNearBottom(el)
    }, [])

    useLayoutEffect(() => {
        pinnedToBottomRef.current = true
        if (enabled) {
            scrollToBottom('auto')
        }
    }, [conversationId, enabled, scrollToBottom])

    useLayoutEffect(() => {
        const last = messages[messages.length - 1]
        if (loading || last?.role === 'user') {
            pinnedToBottomRef.current = true
        }
    }, [loading, messages])

    const scrollSignature = useMemo(() => {
        const last = messages[messages.length - 1]
        return [
            messages.length,
            last?.id,
            last?.content?.length ?? 0,
            last?.streaming ?? false,
            last?.progressLabel ?? '',
            loading,
        ].join('|')
    }, [loading, messages])

    useLayoutEffect(() => {
        if (!enabled) return
        if (messages.length === 0 && !loading) return
        if (pinnedToBottomRef.current) {
            scrollToBottom('auto')
        }
    }, [enabled, loading, messages.length, scrollSignature, scrollToBottom])

    useEffect(() => {
        const content = contentRef.current
        if (!content || !enabled) return

        const ro = new ResizeObserver(() => {
            if (pinnedToBottomRef.current) {
                scrollToBottom('auto')
            }
        })
        ro.observe(content)
        return () => ro.disconnect()
    }, [enabled, scrollToBottom])

    return {
        scrollableNodeProps: {
            ref: scrollableNodeRef,
            onScroll: handleScroll,
        } as const,
        contentRef,
    }
}
